import React, { useRef, useEffect } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import Top from '../Top/Top';
import TemplateParser from '../Templates/TemplateParser';
import { origin } from '../../config';
import { offset, scrollTo, addClass, removeClass } from '../../utils/dom';
import { imageSizeTranslation } from '../../utils/image';
import { translate, prefix, translateObjectDefaultData } from '../../utils/locale';
import { deepClone } from '../../utils/object';
import s from './Page.scss';

const Page = ({ location, magazineListing, listing, data, setup, hashLocation = false }) => {
	if (!data) return null;
	const listingMetaData = translateObjectDefaultData(setup, magazineListing.meta);
	const months = translate(setup, 'months').split(/\r?\n/);

	const pageElement = useRef(null);
	const scrollToHash = () => {
		let imagesLoaded = null;
		const localHashLocation = hashLocation || window.location.hash;

		const allLoaded = () => {
			if (pageElement && pageElement.current) {
				const imgs = pageElement.current.querySelectorAll('img');
				imagesLoaded = [...imgs].length;

				[...imgs].forEach((img) => {
					if (img.complete) imagesLoaded -= 1;
				});
			}

			if (pageElement && pageElement.current && imagesLoaded === 0) {
				// Delay to understand page navigation
				setTimeout(() => {
					const position = offset(document.getElementById(localHashLocation.replace('#', ''))).top - 80;
					scrollTo(500, position);
				}, 300);
			} else {
				setTimeout(allLoaded, 50);
			}
		};

		allLoaded();
	};

	useEffect(() => {
		if (hashLocation || window.location.hash !== '') scrollToHash();

		if (data?.content?.sections && data.content.sections.length > 0) {
			const firstSection = data.content.sections[0];
			if (firstSection.ccBlocksPuff && firstSection.ccBlocksPuff?.attrs?.align === 'full' && firstSection.ccBlocksPuff?.attrs?.transparentMenu) {
				addClass(document.body, 'u_overlayHeader');
				if (firstSection.ccBlocksPuff.attrs.dark) {
					addClass(document.body, 'u_overlayHeader__inverse');
				}
			}
		}

		return () => {
			removeClass(document.body, ['u_overlayHeader', 'u_overlayHeader__inverse']);
		};
	});

	const pageClasses = [s.page_inner];
	if (data.addMenu) pageClasses.push(s.page_inner__withMenu);

	// Check if page contains core/block
	const skipParts = [];
	const metaData = {
		...(data?.content?.meta?.default || {}),
		linkCanonical: `${origin}${prefix(setup, location.payload.path)}`.replace(/\/+$/, ''),
	};

	return (
		<>
			<main role="main">
				<Top setup={setup} pageContent={metaData} paths={data.paths} locationType={location.type} />
				<div className={s.page} ref={pageElement}>
					<div className={pageClasses.join(' ')}>
						{
							data.content && data.content.sections.map((section) => {
								if (Object.keys(section)[0] === 'coreBlock') {
									const whichPart = section.coreBlock.content;
									let partsContent = null;

									data.parts.forEach((part) => {
										if (parseInt(part.meta.id, 10) === parseInt(whichPart, 10)) {
											partsContent = deepClone(part.sections);
											skipParts.push(parseInt(part.meta.id, 10));
										}
									});

									return partsContent ? <TemplateParser sections={partsContent} setup={setup} /> : null;
								}
								return <TemplateParser sections={[section]} setup={setup} />;
							})
						}
						{
							data.parts && data.parts.length > 0 && data.parts.map((section) => {
								if (skipParts.indexOf(parseInt(section.meta.id, 10)) !== -1) return null;
								return <TemplateParser sections={section.sections} setup={setup} />;
							})
						}
					</div>
					{
						listingMetaData.uri === location.payload.path && listing.magazine && (
							<div className={s.magazine}>
								{
									listing.magazine.content.map((article, i) => {
										const articleMetaData = translateObjectDefaultData(setup, article.meta);
										const publishedDate = new Date(articleMetaData.published * 1e3);
										return (
											<Link key={article.meta.default.uri} to={prefix(setup, `${location.payload.path}/${articleMetaData.uri}`)} className={s.magazine_article}>
												<img className={s.magazine_article_image} {...imageSizeTranslation(i === 0 ? '12' : '6', articleMetaData.image)} />
												<time className={s.magazine_article_published} dateTime={`${publishedDate.getFullYear()}-${publishedDate.getMonth() <= 8 ? '0' : ''}${publishedDate.getMonth() + 1}`}>{`${months[publishedDate.getMonth()]}, ${publishedDate.getFullYear()}`}</time>
												<h1 className={[s.magazine_article_title, `u_fontSize__${i === 0 ? 44 : 37}`].join(' ')}>{articleMetaData.title}</h1>
												<TemplateParser sections={article.sections} setup={setup} inline />
												<span className={s.magazine_article_readMore}>{ translate(setup, 'readMore') }</span>
											</Link>
										);
									})
								}
							</div>
						)
					}
				</div>
			</main>
		</>
	);
};

const mapState = ({ environment, location, listing }, { data, currentPath }) => ({
	location,
	data,
	currentPath,
	listing,
	hashLocation: environment.hashLocation,
	setup: environment.setup,
	magazineListing: environment.magazineListing,
});

export default hot(module)(connect(mapState)(Page));
